/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React from 'react';
import {
  renderNumericValue,
  conversionParams,
  getListValueDataData,
  getHistorySource,
  isSourceDataTransfer,
  isUtrvAssuranceComplete,
  isUtrvAssuranceRestated,
  isUtrvPartialAssurance,
  isUtrvAssuranceRejected,
} from '@utils/universalTrackerValue';
import { UtrvStatus } from '../../constants/status';
import TableInputView from '@features/survey/form/view/TableInputView';
import { tableDataToView } from '@utils/valueDataTable';
import {
  UtrvAssuranceStatus,
  ValueHistory,
  ValueDataData,
  ValueDataObject,
  UniversalTrackerValuePlain
} from '@g17eco/types/universalTrackerValue';
import UniversalTrackerValue from '@models/UniversalTrackerValue';
import { NotApplicableReadableText, NotApplicableTypes } from '@constants/status';
import { UtrValueType } from '@g17eco/types/universalTracker';
import { UncontrolledCollapse } from 'reactstrap';
import {
  getTableConfiguration,
  isNumberOrTextValueListType,
  isSingleRowTableType,
  getValueListData,
  getValueListMultiData,
} from '@utils/universalTracker';
import { CollapsibleValueHistoryText } from './CollapsibleValueHistoryText';
import { UnitConfig } from '@g17eco/types/survey';
import UniversalTrackerClassInterface from '../../model/UniversalTrackerClassInterface';
import { MergedValueListOption, ValueListConfig } from '@g17eco/types/initiativeUniversalTracker';

type StatusTextString = string | React.JSX.Element;

interface ValueTrio {
  value: number | undefined;
  unit: string | undefined;
  numberScale: string | undefined;
}

/**
 * Pick the (value, unit, numberScale) trio from one origin so the three
 * fields can never split — splitting is what let GU-7010 render Data Sync
 * rows without their unit. Origin is `valueData.input` when `input.value`
 * is a number, otherwise the top-level fields.
 *
 * The numeric `typeof` check (vs `!== undefined`) is deliberate: a
 * non-number `input.value` would render "N/A" beside an input-origin unit,
 * which is the same mixed-origin bug class.
 *
 * Known leak: `conversionParams` still has `unit || input.unit` fallbacks,
 * so a root-origin trio with `unit: undefined` can pick up `input.unit`.
 * Fix belongs in a follow-up that removes those fallbacks across callers.
 */
export const resolveHistoryValueTrio = (
  utrvLike: UniversalTrackerValue | UniversalTrackerValuePlain | ValueHistory,
): ValueTrio => {
  const v = 'universalTrackerValue' in utrvLike ? utrvLike.universalTrackerValue : utrvLike;
  const input = v.valueData?.input;
  const useInput = input !== undefined && typeof input.value === 'number';
  const source = useInput ? input : (v as Partial<Pick<UniversalTrackerValuePlain, 'value' | 'unit' | 'numberScale'>>);

  return {
    value: typeof source.value === 'number' ? source.value : undefined,
    unit: typeof source.unit === 'string' ? source.unit : undefined,
    numberScale: typeof source.numberScale === 'string' ? source.numberScale : undefined,
  };
};

export class ValueHistoryText {
  private static getValueText({
    history,
    statusTextString,
    universalTracker,
    className,
    username,
  }: {
    history: ValueHistory;
    statusTextString: StatusTextString;
    universalTracker: UniversalTrackerClassInterface;
    className?: string;
    username?: string;
  }) {
    const { sampleSize, valueData } = history;
    const trio = resolveHistoryValueTrio(history);
    const valueString = ValueHistoryText.getValueString(trio, history, valueData, universalTracker, className);

    const sampleSizeString =
      typeof sampleSize === 'number' ? (
        <React.Fragment>
          out of {ValueHistoryText.withUnits({
            utrvLike: history,
            trio: { value: sampleSize, unit: trio.unit, numberScale: trio.numberScale },
            utr: universalTracker,
          })}
        </React.Fragment>
      ) : (
        ''
      );

    return (
      <>
        <div data-testid='action-username'>
          {statusTextString} by <strong>{username}</strong>
        </div>
        <div data-testid='history-text'>
          {valueString} {sampleSizeString}
        </div>
      </>
    );
  }

  private static getValueString(
    trio: ValueTrio,
    h: ValueHistory,
    valueData: UniversalTrackerValuePlain['valueData'] | ValueDataData,
    utr: UniversalTrackerClassInterface,
    className?: string,
  ) {
    if (trio.value === undefined) {
      return valueData &&
        typeof valueData === 'object' &&
        'notApplicableType' in valueData &&
        valueData.notApplicableType === NotApplicableTypes.nr
        ? NotApplicableReadableText[NotApplicableTypes.nr]
        : NotApplicableReadableText[NotApplicableTypes.na];
    }
    return ValueHistoryText.withUnits({ utrvLike: h, trio, utr, className });
  }

  private static withUnits({
    utrvLike,
    trio,
    utr,
    className = '',
    nonNumericClassName = 'strong text-primary',
  }: {
    utrvLike: UniversalTrackerValue | UniversalTrackerValuePlain | ValueHistory;
    trio: { value: number | string | undefined; unit: string | undefined; numberScale: string | undefined };
    utr: UniversalTrackerClassInterface;
    className?: string;
    nonNumericClassName?: string;
  }) {
    if (isNaN(trio.value as number) || !utr || !utr.isNumeric()) {
      return <span className={nonNumericClassName}>{trio.value}</span>;
    }
    const data = conversionParams(utrvLike, utr, trio);
    return renderNumericValue(data, className);
  }

  private static getValueDataText(
    username: string,
    options: MergedValueListOption[],
    h: ValueHistory,
    statusTextString: StatusTextString,
  ) {
    const valueData: ValueDataData | undefined = h.valueData?.data
      ? Array.isArray(h.valueData?.data)
        ? getValueListMultiData(h.valueData.data, options)
        : getValueListData(h.valueData.data, options)
      : undefined;

    if (statusTextString === 'Created' && valueData === undefined) {
      return `New data point ${statusTextString}`;
    }

    const dataString = ValueHistoryText.getDataString(valueData, h);

    return (
      <>
        <div data-testid='action-username'>
          {statusTextString} by <strong>{username}</strong>
        </div>
        <div data-testid='history-text'>{dataString}</div>
      </>
    );
  }

  private static getDataString(valueData: ValueDataData | undefined, h: ValueHistory): React.JSX.Element | string {
    if (valueData === undefined) {
      return <div className='text-ThemeWarningMedium'>{NotApplicableReadableText[NotApplicableTypes.na]}</div>;
    }
    if (h.valueData && h.valueData.notApplicableType === NotApplicableTypes.nr) {
      return <div className='text-ThemeWarningMedium'>{NotApplicableReadableText[NotApplicableTypes.nr]}</div>;
    }
    if (Array.isArray(valueData)) {
      return (
        <ul className='text-ThemeTextMedium'>
          {valueData.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      );
    }

    if (typeof valueData === 'object') {
      return (
        <ul className='text-ThemeTextMedium'>
          {Object.keys(valueData).map((k) => (
            <li key={k}>{valueData[k]}</li>
          ))}
        </ul>
      );
    }

    return <div className='text-ThemeTextMedium'>{valueData}</div>;
  }

  private static getTableView({
    username,
    universalTracker,
    history,
    statusTextString,
    unitConfig,
    utrValueList
  }: {
    username: string;
    universalTracker: UniversalTrackerClassInterface;
    history: ValueHistory;
    statusTextString: StatusTextString;
    unitConfig?: UnitConfig;
    utrValueList?: ValueListConfig<MergedValueListOption>;
  }) {
    const { rows, editRowId } = tableDataToView(history.valueData);

    if (statusTextString === 'Created') {
      return `New data point ${statusTextString}`;
    }

    if (rows.length > 0) {
      return (
        <>
          <div data-testid='action-username'>
            {statusTextString} by <strong>{username}</strong>
          </div>
          <TableInputView
            className={'p-0'}
            utr={universalTracker.getRaw()}
            disabled={true}
            editRowId={editRowId}
            rowData={rows}
            ignoreType={true}
            unitConfig={unitConfig}
            utrValueList={utrValueList}
          />
        </>
      );
    }
    const dataString =
      history.valueData && history.valueData.notApplicableType === NotApplicableTypes.nr
        ? NotApplicableReadableText[NotApplicableTypes.nr]
        : NotApplicableReadableText[NotApplicableTypes.na];
    return (
      <>
        <div data-testid='action-username'>
          {statusTextString} by <strong>{username}</strong>
        </div>
        <div data-testid='history-text' className='text-ThemeWarningMedium'>
          {dataString}
        </div>
      </>
    );
  }

  private static getValueDataList(
    username: string,
    universalTracker: UniversalTrackerClassInterface,
    h: ValueHistory,
    statusTextString: StatusTextString,
  ) {
    let dataString: StatusTextString = '';

    const valueData = getListValueDataData(h) as ValueDataObject;
    const valueKeys = valueData ? Object.keys(valueData) : undefined;

    if (statusTextString === 'Created' && valueData === undefined) {
      return `New data point ${statusTextString}`;
    }

    const isNumericList = universalTracker.getValueType() === UtrValueType.NumericValueList;
    switch (true) {
      case Array.isArray(valueKeys) && valueKeys.length > 0: {
        const options = universalTracker.getValueListOptions();
        // Per-item values share the history's unit/numberScale.
        const listTrio = resolveHistoryValueTrio(h);
        dataString = (
          <ul className={`keyValue ${isNumericList ? 'inline-rows' : ''}`}>
            {valueKeys.map((k: string) => (
              <li key={k}>
                <div className='value-label fw-semibold text-ThemeHeadingMedium'>{getValueListData(k, options)}:</div>
                <div className={'value-data-value text-ThemeTextMedium'}>
                  {ValueHistoryText.withUnits({
                    utrvLike: h,
                    trio: { value: valueData[k], unit: listTrio.unit, numberScale: listTrio.numberScale },
                    utr: universalTracker,
                    nonNumericClassName: '',
                  })}
                </div>
              </li>
            ))}

            {isNumericList && (
              <li key={'total'}>
                <div className='value-label strong text-ThemeHeadingMedium'>Total:</div>
                <div className={'value-data-value text-ThemeTextMedium'}>
                  {ValueHistoryText.getValueString(listTrio, h, valueData, universalTracker)}
                </div>
              </li>
            )}
          </ul>
        );
        break;
      }
      case h.valueData && h.valueData.notApplicableType === NotApplicableTypes.nr:
        dataString = (
          <span className='text-ThemeWarningMedium'>{NotApplicableReadableText[NotApplicableTypes.nr]}</span>
        );
        break;
      case valueData === undefined:
      default:
        dataString = (
          <span className='text-ThemeWarningMedium'>{NotApplicableReadableText[NotApplicableTypes.na]}</span>
        );
        break;
    }

    return (
      <>
        <div data-testid='action-username'>
          {statusTextString} by <strong>{username}</strong>
        </div>
        <div data-testid='history-text'>{dataString}</div>
      </>
    );
  }

  public static getTextString({
    universalTracker,
    history,
    statusTextString,
    username,
    unitConfig,
    utrValueList
  }: {
    universalTracker: UniversalTrackerClassInterface;
    history: ValueHistory;
    statusTextString: StatusTextString;
    username: string;
    unitConfig?: UnitConfig;
    utrValueList?: ValueListConfig<MergedValueListOption>;
  }) {
    const valueType = history.valueType ?? universalTracker.getValueType();
    switch (valueType) {
      case 'date':
      case 'text':
      case 'valueList':
      case 'valueListMulti':
        return ValueHistoryText.getValueDataText(username, utrValueList?.options ?? universalTracker.getValueListOptions(), history, statusTextString);
      case 'textValueList':
      case 'numericValueList':
        return ValueHistoryText.getValueDataList(username, universalTracker, history, statusTextString);
      case 'table':
        return ValueHistoryText.getTableView({ username, universalTracker, history, statusTextString, unitConfig, utrValueList });
      case 'number':
      case 'percentage':
      case 'sample':
      default:
        return ValueHistoryText.getValueText({ history, statusTextString, universalTracker, username });
    }
  }

  public static partialAssuranceProvenance(params: {
    history: ValueHistory;
    universalTracker: UniversalTrackerClassInterface;
    utrValueList?: ValueListConfig<MergedValueListOption>;
  }) {
    const { history, universalTracker, utrValueList } = params;

    if (history.assuranceStatus !== UtrvAssuranceStatus.Partial) {
      return null;
    }
    const { valueData, assuranceFields = [] } = history;
    if (isNumberOrTextValueListType(universalTracker)) {
      const data = valueData?.data ?? {};
      const listOptions = universalTracker.getValueListOptions();

      // Per-option values share the history's unit/numberScale.
      const partialTrio = resolveHistoryValueTrio(history);
      const assuredListItems = Object.entries(data)
        .map(([code, value]) => {
          const isAssured = assuranceFields.some((f) => f.code === code);
          if (isAssured) {
            const option = listOptions.find((o) => o.code === code);
            const valueDisplay = this.withUnits({
              utrvLike: history,
              trio: {
                value: value as number | string | undefined,
                unit: partialTrio.unit,
                numberScale: partialTrio.numberScale,
              },
              utr: universalTracker,
              className: 'text-ThemeTextMedium',
              nonNumericClassName: 'text-ThemeTextMedium',
            });
            return (
              <li key={code}>
                <div className='fw-semibold text-ThemeHeadingMedium'>{option?.name ?? code}:</div>
                <div className={'text-ThemeTextMedium'}>{valueDisplay}</div>
              </li>
            );
          }
          return null;
        })
        .filter(Boolean);

      return <ol className='partial-assurance-list pl-0 m-0'>{assuredListItems}</ol>;
    }

    const columns = getTableConfiguration(universalTracker)?.columns ?? [];

    if (isSingleRowTableType(universalTracker)) {
      // Single row table store data in the first row
      const data = valueData?.table?.[0] ?? [];

      // Per-cell values share the history's unit/numberScale.
      const rowTrio = resolveHistoryValueTrio(history);
      const assuredColumnItems = data
        .map(({ code, value }) => {
          const isAssured = assuranceFields.some((f) => f.code === code);
          if (isAssured) {
            const column = columns.find((c) => c.code === code);
            const valueDisplay = Array.isArray(value)
              ? value.join(', ')
              : this.withUnits({
                  utrvLike: history,
                  trio: { value, unit: rowTrio.unit, numberScale: rowTrio.numberScale },
                  utr: universalTracker,
                  className: 'text-ThemeTextMedium',
                  nonNumericClassName: 'text-ThemeTextMedium',
                });
            return (
              <li key={code}>
                <div className='fw-semibold text-ThemeHeadingMedium'>{column?.name ?? code}:</div>
                <div className={'text-ThemeTextMedium'}>{valueDisplay}</div>
              </li>
            );
          }
          return null;
        })
        .filter(Boolean);

      return <ol className='partial-assurance-list pl-0 m-0'>{assuredColumnItems}</ol>;
    }

    if (universalTracker.getValueType() === UtrValueType.Table) {
      const { rows, editRowId } = tableDataToView(valueData);
      const assuredRows = rows.filter(({ id, data }) => {
        return data.every(({ code }) => assuranceFields.some((f) => f.code === code && f.rowIndex === id));
      });

      return (
        <TableInputView
          className='mb-4 mt-2'
          utr={universalTracker.getRaw()}
          disabled={true}
          editRowId={editRowId}
          rowData={assuredRows}
          ignoreType={true}
          utrValueList={utrValueList}
        />
      );
    }

    return null;
  }

  private static getStatusText({ status, history }: { status: string; history: ValueHistory }) {
    if (status === 'Verified' && isUtrvAssuranceComplete(history.assuranceStatus)) {
      return 'Assured';
    }

    if (isUtrvAssuranceRejected(history.assuranceStatus)) {
      return 'Disputed';
    }

    if (isUtrvAssuranceRestated(history.assuranceStatus)) {
      return 'Restated';
    }

    if (status === 'Updated') {
      return 'Submitted';
    }

    return status;
  }

  /** Main entry point */
  public static getHistoryText({
    className,
    status,
    history,
    username,
    universalTracker,
    unitConfig,
    utrValueList
  }: {
    className: string;
    status: string;
    history: ValueHistory;
    username: string;
    universalTracker: UniversalTrackerClassInterface;
    unitConfig?: UnitConfig;
    utrValueList?: ValueListConfig<MergedValueListOption>;
  }) {
    if (isUtrvPartialAssurance(history.assuranceStatus)) {
      const toggleId = `partial-assurance-${history._id}`;
      return (
        <div className='w-100'>
          <div
            id={toggleId}
            className='p-0 text-ThemeTextDark partial-assurance-toggle'
            onClick={(e) => {
              e.currentTarget.classList.toggle('active');
            }}
            data-testid='action-username'
          >
            <span className={className}>
              <strong>Partially assured</strong>
            </span>{' '}
            by <strong>{username}</strong> <i className='fa-light fa-caret-down ml-2 text-ThemeIconSecondary' />
          </div>
          <UncontrolledCollapse toggler={`#${toggleId}`}>
            {this.partialAssuranceProvenance({ history, universalTracker, utrValueList })}
          </UncontrolledCollapse>
        </div>
      );
    }

    const statusText = ValueHistoryText.getStatusText({ status, history });
    const isImported = history.valueData?.isImported === true;
    const source = getHistorySource(history.valueData);
    const isDataTransfer = source !== undefined && isSourceDataTransfer(source);
    const showImportLabel = isImported && !isDataTransfer && history.action !== UtrvStatus.Verified;
    const importLabel = showImportLabel ? ' (via import file)' : '';
    const statusTextString = (
      <span className={className}>
        <strong>{statusText}</strong>
        {importLabel}{' '}
      </span>
    );
    if (['Created', 'Rejected', 'Verified', 'Assured', 'Disputed'].includes(statusText)) {
      return (
        <div data-testid='action-username'>
          {statusTextString} by <strong>{username}</strong>
        </div>
      );
    }
    return (
      <CollapsibleValueHistoryText
        universalTracker={universalTracker}
        history={history}
        statusTextString={statusTextString}
        username={username}
        unitConfig={unitConfig}
        utrValueList={utrValueList}
      />
    );
  }

  public static getCompositeHistoryText(
    className: string | undefined,
    statusText: string,
    history: ValueHistory,
    universalTracker: UniversalTrackerClassInterface,
    username: string,
  ) {
    switch (statusText) {
      case 'Rejected':
        return 'There is not enough information to calculate data point';
      case 'Updated':
        return 'Calculation reset due to changes in underlying data points';
      default:
      case 'Verified':
        return ValueHistoryText.getValueText({
          history,
          statusTextString: 'successfully calculated',
          universalTracker,
          className,
          username,
        });
    }
  }
}
